import type { TableColumnType } from 'antd'
import type { FilterDropdownProps } from 'antd/es/table/interface'

import { useQuery } from '@tanstack/react-query'
import {
  CalendarOutlined,
  CloseCircleOutlined,
  FileImageOutlined,
  FilterOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { Button, Checkbox, DatePicker, Form, Input, Radio, Spin, Tag, Tooltip } from 'antd'
import React, { useCallback, useState } from 'react'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'

import $api, { API_URL } from '../../../API'
import type { HelpSection } from '../../../components/page'
import { PageContainer } from '../../../components/page/page-container'
import { PageHeader } from '../../../components/page/page-header'
import { HelpDrawerLayout, HelpDrawerTrigger } from '../../../components/page/help-drawer'
import { PageGrid } from '../../../components/page/page-grid'
import type { Attachment } from '../../../store/previewer/types'
import { useAppDispatch } from '../../../store'
import * as previewActions from '../../../store/previewer'

import './transactions.css'

interface FilterOptionsData {
  codes: Array<{ code: string; description: string | null }>
  types: string[]
}

interface TransactionInfo {
  id: string
  file_id: string
  account_id: string
  bank_ref_number: string
  amount: number
  code: string | null
  type: string | null
  text: string | null
  customer_reference_number: string | null
  account_number: string
  date: string
  created_at: string
  x937_check_id: string | null
  x937_paid_date: string | null
  group_number: string | null
  group_name: string | null
  group_location: string | null
}

interface TransactionFilters {
  dateFrom?: string
  dateTo?: string
  accountNumber?: string
  codes?: string[]
  types?: string[]
  amount?: string
  bankRefNumber?: string
  customerRefNumber?: string
  description?: string
  paidDateFrom?: string
  paidDateTo?: string
  direction?: 'credit' | 'debit'
  groupNumber?: string
  groupName?: string
  groupLocations?: string[]
}

interface Filter {
  sort?: string
  order?: 'ASC' | 'DESC'
  page: number
  perPage: number
  filters: TransactionFilters
}

interface QueryData {
  transactions: TransactionInfo[]
  count: number
}

interface MiniDashboardData {
  credits_count: number
  credits_total: number
  debits_count: number
  debits_total: number
  total_count: number
  null_type_count: number
  latest_date: string
}

const currencyFormatter = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)
}

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat('en-US').format(num)
}

interface MiniDashboardProps {
  data?: MiniDashboardData
  isLoading?: boolean
}

function MiniDashboard({ data, isLoading }: MiniDashboardProps): React.JSX.Element {
  if (isLoading) {
    return (
      <div className='flex flex-row gap-4 shrink-0'>
        <div className='flex flex-row gap-8 rounded-lg bg-gray-100 px-4 sm:px-8 py-2 select-none relative min-h-[60px] min-w-[200px]'>
          <span className='font-lato tracking-wide text-[9.5px] font-bold uppercase text-gray-50'>
            Loading
          </span>
          <Spin
            spinning
            className='absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-[calc(-50%_+_3px)]'
          />
        </div>
      </div>
    )
  }

  const latestDate = data?.latest_date
    ? new Date(data.latest_date).toLocaleDateString('en-US')
    : 'N/A'

  const creditsCount = data?.credits_count ?? 0
  const debitsCount = data?.debits_count ?? 0
  const totalCount = data?.total_count ?? 0
  const nullTypeCount = data?.null_type_count ?? 0
  const hasDiscrepancy = creditsCount + debitsCount !== totalCount

  return (
    <div className='flex flex-col gap-2 shrink-0'>
      <div className='flex flex-row gap-4'>
        <div className='flex flex-row flex-wrap gap-4 sm:gap-8 px-4 sm:px-8 py-2 bg-gray-100 rounded-lg'>
          <span className='font-lato tracking-wide text-[9.5px] font-bold uppercase text-gray-800 w-full sm:w-auto'>
            Latest Day ({latestDate})
          </span>

          <Tooltip title='Credits (codes < 400)'>
            <div className='flex flex-col justify-start items-center'>
              <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                Credits
              </div>
              <div className='text-sm sm:text-lg text-gray-700 font-lato whitespace-nowrap'>
                {formatNumber(creditsCount)} / {currencyFormatter((data?.credits_total ?? 0) / 100)}
              </div>
            </div>
          </Tooltip>

          <Tooltip title='Debits (codes >= 400)'>
            <div className='flex flex-col justify-start items-center'>
              <div className='font-lato tracking-wide text-[9.5px] uppercase text-gray-500'>
                Debits
              </div>
              <div className='text-sm sm:text-lg text-gray-700 font-lato whitespace-nowrap'>
                {formatNumber(debitsCount)} / {currencyFormatter((data?.debits_total ?? 0) / 100)}
              </div>
            </div>
          </Tooltip>

          {hasDiscrepancy && (
            <Tooltip
              title={`Warning: ${nullTypeCount} transaction(s) with NULL type code. Total: ${totalCount}, Credits: ${creditsCount}, Debits: ${debitsCount}`}
            >
              <div className='flex flex-col justify-start items-center'>
                <div className='font-lato tracking-wide text-[9.5px] uppercase text-orange-600 flex items-center gap-1'>
                  <span>⚠ Other</span>
                </div>
                <div className='text-sm sm:text-lg text-orange-600 font-lato'>
                  {formatNumber(totalCount - creditsCount - debitsCount)}
                </div>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

function TextFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm
}: FilterDropdownProps): React.JSX.Element {
  const value = selectedKeys[0] ? String(selectedKeys[0]) : ''

  return (
    <div className='text-filter p-3'>
      <Input
        placeholder='Search...'
        value={value}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        className='mb-2'
      />
      <div className='flex gap-2 justify-end'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  )
}

function AmountFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm
}: FilterDropdownProps): React.JSX.Element {
  const value = selectedKeys[0] ? String(selectedKeys[0]) : ''

  return (
    <div className='text-filter p-3'>
      <Input
        placeholder='Search...'
        prefix={<span className='text-gray-400'>$</span>}
        value={value}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        className='mb-2'
      />
      <div className='flex gap-2 justify-end'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  )
}

interface CodeOption {
  value: string
  label: string | null
}

interface CodeFilterDropdownProps extends FilterDropdownProps {
  options: CodeOption[]
}

function CodeFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  options
}: CodeFilterDropdownProps): React.JSX.Element {
  const selectedSet = new Set(selectedKeys.map(String))

  const handleToggle = (value: string): void => {
    const newSet = new Set(selectedSet)
    if (newSet.has(value)) {
      newSet.delete(value)
    } else {
      newSet.add(value)
    }
    setSelectedKeys(Array.from(newSet))
  }

  return (
    <div className='multiselect-filter p-3'>
      <div className='max-h-[200px] overflow-y-auto mb-2'>
        {options.map(opt => (
          <div key={opt.value} className='py-1 flex items-start'>
            <Checkbox
              checked={selectedSet.has(opt.value)}
              onChange={() => handleToggle(opt.value)}
              className='items-start'
            >
              <span className='inline-flex items-center gap-1 text-left font-normal'>
                <span>{opt.value}</span>
                {opt.label && <span className='text-gray-500 text-xs'>{opt.label}</span>}
              </span>
            </Checkbox>
          </div>
        ))}
      </div>
      <div className='flex gap-2 justify-end border-t border-gray-100 pt-2'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  )
}

interface TypeFilterDropdownProps extends FilterDropdownProps {
  options: string[]
}

function TypeFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  options
}: TypeFilterDropdownProps): React.JSX.Element {
  const selectedSet = new Set(selectedKeys.map(String))

  const handleToggle = (value: string): void => {
    const newSet = new Set(selectedSet)
    if (newSet.has(value)) {
      newSet.delete(value)
    } else {
      newSet.add(value)
    }
    setSelectedKeys(Array.from(newSet))
  }

  return (
    <div className='multiselect-filter p-3'>
      <div className='max-h-[200px] overflow-y-auto mb-2'>
        {options.map(opt => (
          <div key={opt} className='py-1'>
            <Checkbox checked={selectedSet.has(opt)} onChange={() => handleToggle(opt)}>
              {opt}
            </Checkbox>
          </div>
        ))}
      </div>
      <div className='flex gap-2 justify-end border-t border-gray-100 pt-2'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  )
}

function DirectionFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm
}: FilterDropdownProps): React.JSX.Element {
  const selected = selectedKeys[0] ? String(selectedKeys[0]) : ''

  const options = [
    { value: 'credit', label: 'Credit' },
    { value: 'debit', label: 'Debit' }
  ]

  return (
    <div className='direction-filter' style={{ width: 200, padding: '14px 12px 14px' }}>
      <Radio.Group
        value={selected || undefined}
        onChange={e => {
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }}
        className='flex flex-col items-left mb-3 gap-2'
      >
        {options.map(opt => (
          <Radio key={opt.value} value={opt.value}>
            <Tag color={opt.value === 'credit' ? 'green' : 'red'} style={{ fontWeight: 400 }}>
              {opt.label}
            </Tag>
          </Radio>
        ))}
      </Radio.Group>
      <div className='flex gap-2 justify-end border-t border-gray-100 pt-2'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  )
}

const GROUP_LOCATIONS = ['CA', 'WI', 'TN'] as const

function GroupLocationFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm
}: FilterDropdownProps): React.JSX.Element {
  const selectedSet = new Set(selectedKeys.map(String))

  const handleToggle = (value: string): void => {
    const newSet = new Set(selectedSet)
    if (newSet.has(value)) {
      newSet.delete(value)
    } else {
      newSet.add(value)
    }
    setSelectedKeys(Array.from(newSet))
  }

  const colorMap: Record<string, string> = {
    CA: 'green',
    WI: 'blue',
    TN: 'red'
  }

  return (
    <div className='group-location-filter p-3' style={{ width: 120 }}>
      <div className='mb-2'>
        {GROUP_LOCATIONS.map(loc => (
          <div key={loc} className='py-1 flex items-center'>
            <Checkbox checked={selectedSet.has(loc)} onChange={() => handleToggle(loc)}>
              <Tag color={colorMap[loc] ?? 'default'}>{loc}</Tag>
            </Checkbox>
          </div>
        ))}
      </div>
      <div className='flex gap-2 justify-end border-t border-gray-100 pt-2'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => confirm()}>
          OK
        </Button>
      </div>
    </div>
  )
}

function DateFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  close: _close
}: FilterDropdownProps): React.JSX.Element {
  const fromRaw = selectedKeys[0] ?? undefined
  const toRaw = selectedKeys[1] ?? undefined

  const from = fromRaw ? dayjs(String(fromRaw)) : undefined
  const to = toRaw ? dayjs(String(toRaw)) : undefined

  const setFrom = useCallback(
    (input: Dayjs | null) => {
      const value = input ? input.startOf('day').toISOString() : undefined
      setSelectedKeys([value as React.Key, toRaw!])
    },
    [setSelectedKeys, toRaw]
  )

  const setTo = useCallback(
    (input: Dayjs | null) => {
      const value = input ? input.endOf('day').toISOString() : undefined
      setSelectedKeys([fromRaw!, value as React.Key])
    },
    [setSelectedKeys, fromRaw]
  )

  return (
    <div className='date-filter'>
      <div className='flex'>
        <Form
          layout='horizontal'
          className='flex flex-col pt-5 pr-3 pb-0 pl-5 w-full'
          labelAlign='left'
          labelCol={{
            className: 'w-12'
          }}
        >
          <Form.Item label='From' colon={false} className='mb-3'>
            <DatePicker
              placeholder={dayjs().subtract(1, 'week').format('YYYY-MM-DD')}
              value={from}
              onChange={setFrom}
              allowClear
            />
          </Form.Item>

          <Form.Item label='To' colon={false} className='mb-4'>
            <DatePicker
              placeholder={dayjs().format('YYYY-MM-DD')}
              value={to}
              onChange={setTo}
              allowClear
            />
          </Form.Item>
        </Form>

        <ul className='flex flex-col pt-6 pr-5 pb-3 pl-2 border-l border-l-gray-100'>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().subtract(1, 'day').startOf('day').toISOString(),
                  dayjs().subtract(1, 'day').endOf('day').toISOString()
                ])
                confirm()
              }}
            >
              Yesterday
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().subtract(7, 'days').startOf('day').toISOString(),
                  dayjs().endOf('day').toISOString()
                ])
                confirm()
              }}
            >
              Last 7 days
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                setSelectedKeys([
                  dayjs().subtract(30, 'days').startOf('day').toISOString(),
                  dayjs().endOf('day').toISOString()
                ])
                confirm()
              }}
            >
              Last 30 days
            </Button>
          </li>
        </ul>
      </div>

      <div className='flex gap-2 justify-end px-5 pt-3 pb-3 border-t border-t-gray-100'>
        <Button
          size='small'
          onClick={() => {
            setSelectedKeys([])
            confirm()
          }}
        >
          Reset
        </Button>
        <Button
          type='primary'
          size='small'
          onClick={() => {
            confirm()
          }}
        >
          OK
        </Button>
      </div>
    </div>
  )
}

const transactionsHelpSections: HelpSection[] = [
  {
    content: (
      <p>
        Read-only view of bank transactions imported from Pinnacle via BAI2 files. Look up credits,
        debits, and associated check images across accounts.
      </p>
    ),
    dividerAfter: true
  },
  {
    title: 'Refresh / Sync',
    content: (
      <ul className='list-disc pl-4 space-y-1'>
        <li>Loads on page open; refreshes on filter or pagination change.</li>
        <li>No auto-refresh or live updates.</li>
      </ul>
    )
  },
  {
    title: 'Responsibilities',
    content: <p>Finance and operations teams reconciling daily bank activity.</p>
  },
  {
    title: 'Release / Version',
    content: <p>Introduced with the Pinnacle lockbox integration.</p>,
    dividerAfter: true
  },
  {
    title: 'Columns',
    content: (
      <ul className='list-disc pl-4 space-y-1'>
        <li>
          <strong>Date</strong> — Transaction date. Date range filter.
        </li>
        <li>
          <strong>Account Number</strong> — Bank account. Text filter.
        </li>
        <li>
          <strong>Code</strong> — BAI code. Green (+) credits, red (-) debits. Multi-select.
        </li>
        <li>
          <strong>Type</strong> — Transaction type. Multi-select.
        </li>
        <li>
          <strong>Amount</strong> — Dollar amount. Text filter.
        </li>
        <li>
          <strong>Bank / Customer Ref</strong> — Reference identifiers. Text filters.
        </li>
        <li>
          <strong>Paid Date</strong> — Check paid date. View X.937 images when available.
        </li>
        <li>
          <strong>Description</strong> — Transaction text. Text filter.
        </li>
        <li>
          <strong>Group Number / Location</strong> — Matched group info. Location color-coded.
        </li>
      </ul>
    ),
    dividerAfter: true
  },
  {
    title: 'Available Actions',
    content: (
      <ul className='list-disc pl-4 space-y-0.5'>
        <li>View Checks — preview X.937 images (Paid Date column).</li>
        <li>Clear All Filters — reset all active filters.</li>
      </ul>
    ),
    dividerAfter: true
  },
  {
    title: 'Workflow',
    content: (
      <ol className='list-decimal pl-4 space-y-0.5'>
        <li>Review dashboard summary for latest-day totals.</li>
        <li>Filter by date, account, code, type, or amount.</li>
        <li>View check images via the icon in Paid Date column.</li>
        <li>Adjust pagination as needed.</li>
        <li>Clear all filters to start a new search.</li>
      </ol>
    )
  }
]

export function PinnacleTransactionsPage(): React.JSX.Element {
  const appDispatch = useAppDispatch()

  const [filter, setFilter] = useState<Filter>({
    sort: 'transaction_date',
    order: 'DESC',
    page: 0,
    perPage: 30,
    filters: {}
  })

  const query = useQuery<unknown, unknown, QueryData, ['pinnacle-transactions', Filter]>({
    queryKey: ['pinnacle-transactions', filter],
    async queryFn() {
      const { data } = await $api.get<QueryData>('pinnacle/transactions', {
        params: {
          ...filter,
          ...filter.filters
        }
      })

      return {
        transactions: data.transactions ?? [],
        count: data.count ?? 0
      }
    }
  })

  const filterOptionsQuery = useQuery<
    unknown,
    unknown,
    FilterOptionsData,
    ['pinnacle-filter-options']
  >({
    queryKey: ['pinnacle-filter-options'],
    async queryFn() {
      const { data } = await $api.get<FilterOptionsData>('pinnacle/transactions/filter-options')
      return data
    }
  })

  const dashboardQuery = useQuery<unknown, unknown, MiniDashboardData, ['pinnacle-dashboard']>({
    queryKey: ['pinnacle-dashboard'],
    async queryFn() {
      const { data } = await $api.get<MiniDashboardData>('pinnacle/lockbox/transactions/dashboard')
      return data
    }
  })

  const activeFiltersCount = React.useMemo(() => {
    const { filters } = filter
    let count = 0
    if (filters.dateFrom || filters.dateTo) count++
    if (filters.accountNumber) count++
    if (filters.codes && filters.codes.length > 0) count++
    if (filters.types && filters.types.length > 0) count++
    if (filters.amount) count++
    if (filters.bankRefNumber) count++
    if (filters.customerRefNumber) count++
    if (filters.description) count++
    if (filters.paidDateFrom || filters.paidDateTo) count++
    if (filters.direction) count++
    if (filters.groupNumber) count++
    if (filters.groupName) count++
    if (filters.groupLocations && filters.groupLocations.length > 0) count++
    return count
  }, [filter])

  const handleResetAllFilters = useCallback(() => {
    setFilter(f => ({
      ...f,
      page: 0,
      filters: {}
    }))
  }, [])

  const handlePropagatedActionClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.stopPropagation()
    },
    []
  )

  const handlePdfPreviewClick = useCallback(
    (file: Attachment) => (): void => {
      appDispatch(previewActions.open(file))
    },
    [appDispatch]
  )

  const token = window.localStorage.getItem('token')

  const buildFileHref = useCallback(
    (checkId: string) => {
      return `${API_URL}/pinnacle/x937/checks/${checkId}/images?token=${encodeURIComponent(token ?? '')}`
    },
    [token]
  )

  const codeOptions: CodeOption[] = (filterOptionsQuery.data?.codes ?? []).map(c => ({
    value: c.code,
    label: c.description
  }))

  const typeOptions: string[] = filterOptionsQuery.data?.types ?? []

  const columns: Array<TableColumnType<TransactionInfo>> = [
    {
      key: 'date',
      dataIndex: 'date',
      title: 'DATE',
      align: 'left',
      render: (value: string) => dayjs(value).format('MM/DD/YYYY'),
      filterDropdown: DateFilterDropdown,
      filterIcon: () => <CalendarOutlined />,
      filteredValue:
        filter.filters.dateFrom || filter.filters.dateTo
          ? ([filter.filters.dateFrom, filter.filters.dateTo].filter(Boolean) as React.Key[])
          : null,
      onFilter: () => true
    },
    {
      key: 'account_number',
      dataIndex: 'account_number',
      title: 'ACCOUNT NUMBER',
      align: 'left',
      width: 150,
      filterDropdown: TextFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.accountNumber ? [filter.filters.accountNumber] : null,
      onFilter: () => true
    },
    {
      key: 'direction',
      title: 'DIRECTION',
      align: 'center',
      filterDropdown: DirectionFilterDropdown,
      filterIcon: () => <FilterOutlined />,
      filteredValue: filter.filters.direction ? [filter.filters.direction] : null,
      onFilter: () => true,
      render: (_value: unknown, record: TransactionInfo) => {
        if (!record.code) return <span className='text-gray-400 font-lato'>—</span>

        const isCredit = Number(record.code) < 400

        return <Tag color={isCredit ? 'green' : 'red'}>{isCredit ? 'Credit' : 'Debit'}</Tag>
      }
    },
    {
      key: 'amount',
      dataIndex: 'amount',
      title: 'AMOUNT',
      align: 'right',
      filterDropdown: AmountFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.amount ? [filter.filters.amount] : null,
      onFilter: () => true,
      render: (value: number) => currencyFormatter(value / 100)
    },
    {
      key: 'code',
      dataIndex: 'code',
      title: 'CODE',
      align: 'center',
      filterDropdown: (props: FilterDropdownProps) => (
        <CodeFilterDropdown {...props} options={codeOptions} />
      ),
      filterIcon: () => <FilterOutlined />,
      filteredValue: filter.filters.codes ?? null,
      onFilter: () => true,
      render: (value: string | null) => value ?? <span className='text-gray-400 font-lato'>—</span>
    },
    {
      key: 'type',
      dataIndex: 'type',
      title: 'TYPE',
      align: 'left',
      width: 190,
      filterDropdown: (props: FilterDropdownProps) => (
        <TypeFilterDropdown {...props} options={typeOptions} />
      ),
      filterIcon: () => <FilterOutlined />,
      filteredValue: filter.filters.types ?? null,
      onFilter: () => true,
      render: (value: string | null) => value ?? <span className='text-gray-400 font-lato'>—</span>
    },
    {
      key: 'text',
      dataIndex: 'text',
      title: 'DESCRIPTION',
      align: 'left',
      width: 220,
      filterDropdown: TextFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.description ? [filter.filters.description] : null,
      onFilter: () => true,
      render: (value: string | null) => value ?? <span className='text-gray-400 font-lato'>—</span>
    },
    {
      key: 'bank_ref_number',
      dataIndex: 'bank_ref_number',
      title: 'BANK REF NUMBER',
      align: 'left',
      width: 180,
      filterDropdown: TextFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.bankRefNumber ? [filter.filters.bankRefNumber] : null,
      onFilter: () => true
    },
    {
      key: 'customer_reference_number',
      dataIndex: 'customer_reference_number',
      title: 'CUSTOMER REF NUMBER',
      align: 'left',
      width: 180,
      filterDropdown: TextFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.customerRefNumber ? [filter.filters.customerRefNumber] : null,
      onFilter: () => true,
      render: (value: string | null) => value ?? <span className='text-gray-400 font-lato'>—</span>
    },
    {
      title: 'Paid Date',
      key: 'x937_paid_date',
      dataIndex: 'x937_paid_date',
      filterDropdown: DateFilterDropdown,
      filterIcon: () => <CalendarOutlined />,
      filteredValue:
        filter.filters.paidDateFrom || filter.filters.paidDateTo
          ? ([filter.filters.paidDateFrom, filter.filters.paidDateTo].filter(
              Boolean
            ) as React.Key[])
          : null,
      onFilter: () => true,
      render(value: string, record) {
        if (!value || !record.x937_check_id) {
          return <span className='text-gray-400 font-lato'>—</span>
        }

        return (
          <div className='flex flex-row gap-3 justify-center items-center'>
            {dayjs(value).format('MM/DD/YYYY')}

            <div onClick={handlePropagatedActionClick}>
              <Tooltip title='View Checks'>
                <button
                  type='button'
                  onClick={handlePdfPreviewClick({
                    mimeType: 'image/tiff',
                    name: 'Check images',
                    href: buildFileHref(record.x937_check_id)
                  })}
                >
                  <FileImageOutlined className='text-base text-blue-500 cursor-pointer hover:opacity-75 p-1' />
                </button>
              </Tooltip>
            </div>
          </div>
        )
      }
    },
    {
      key: 'group_number',
      dataIndex: 'group_number',
      title: 'GROUP NUMBER',
      align: 'left',
      filterDropdown: TextFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.groupNumber ? [filter.filters.groupNumber] : null,
      onFilter: () => true,
      render: (value: string | null) => value ?? <span className='text-gray-400 font-lato'>—</span>
    },
    {
      key: 'group_name',
      dataIndex: 'group_name',
      title: 'GROUP NAME',
      align: 'left',
      width: 100,
      filterDropdown: TextFilterDropdown,
      filterIcon: () => <SearchOutlined />,
      filteredValue: filter.filters.groupName ? [filter.filters.groupName] : null,
      onFilter: () => true,
      render: (value: string | null) => value ?? <span className='text-gray-400 font-lato'>—</span>
    },
    {
      key: 'group_location',
      dataIndex: 'group_location',
      title: 'GROUP LOCATION',
      align: 'center',
      filterDropdown: GroupLocationFilterDropdown,
      filterIcon: () => <FilterOutlined />,
      filteredValue: filter.filters.groupLocations ?? null,
      onFilter: () => true,
      render: (value: string | null) => {
        if (!value) return <span className='text-gray-400 font-lato'>—</span>

        const colorMap: Record<string, string> = {
          CA: 'green',
          WI: 'blue',
          TN: 'red'
        }

        return <Tag color={colorMap[value] ?? 'default'}>{value}</Tag>
      }
    }
  ]

  return (
    <HelpDrawerLayout title='Transactions' sections={transactionsHelpSections}>
      <PageContainer className='space-y-4 pinnacle-transactions-page'>
        <div className='flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start'>
          <PageHeader suffix={<HelpDrawerTrigger />} />
          <MiniDashboard data={dashboardQuery.data} isLoading={dashboardQuery.isLoading} />
        </div>

        <PageGrid<TransactionInfo>
          wrapperClassName='border border-slate-200 rounded-2xl'
          scroll={{ x: 'max-content' }}
          toolbar={
            activeFiltersCount > 0 ? (
              <div
                className='flex items-center justify-between text-sm'
                style={{ color: '#2663eb' }}
              >
                <div className='flex items-center gap-2'>
                  <FilterOutlined />
                  <span>
                    {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} applied
                  </span>
                </div>
                <button
                  type='button'
                  onClick={handleResetAllFilters}
                  className='flex items-center gap-1 hover:opacity-80 bg-transparent border-none cursor-pointer p-0'
                  style={{ font: 'inherit', color: '#2663eb' }}
                >
                  <CloseCircleOutlined />
                  Clear all
                </button>
              </div>
            ) : undefined
          }
          rowKey='id'
          dataSource={query.data?.transactions}
          loading={query.isLoading}
          columns={columns}
          getPopupContainer={trigger => trigger.parentElement || document.body}
          onChange={(_pagination, filters) => {
            const dateFrom = filters.date?.[0] ? String(filters.date[0]) : undefined
            const dateTo = filters.date?.[1] ? String(filters.date[1]) : undefined
            const description = filters.text?.[0] ? String(filters.text[0]) : undefined
            const customerRefNumber = filters.customer_reference_number?.[0]
              ? String(filters.customer_reference_number[0])
              : undefined
            const amount = filters.amount?.[0] ? String(filters.amount[0]) : undefined
            const bankRefNumber = filters.bank_ref_number?.[0]
              ? String(filters.bank_ref_number[0])
              : undefined
            const accountNumber = filters.account_number?.[0]
              ? String(filters.account_number[0])
              : undefined
            const codes = filters.code?.length ? filters.code.map(String) : undefined
            const types = filters.type?.length ? filters.type.map(String) : undefined
            const paidDateFrom = filters.x937_paid_date?.[0]
              ? String(filters.x937_paid_date[0])
              : undefined
            const paidDateTo = filters.x937_paid_date?.[1]
              ? String(filters.x937_paid_date[1])
              : undefined
            const direction = filters.direction?.[0]
              ? (String(filters.direction[0]) as 'credit' | 'debit')
              : undefined
            const groupNumber = filters.group_number?.[0]
              ? String(filters.group_number[0])
              : undefined
            const groupName = filters.group_name?.[0] ? String(filters.group_name[0]) : undefined
            const groupLocations = filters.group_location?.length
              ? filters.group_location.map(String)
              : undefined

            setFilter(f => ({
              ...f,
              page: 0,
              filters: {
                ...f.filters,
                dateFrom,
                dateTo,
                description,
                customerRefNumber,
                amount,
                bankRefNumber,
                accountNumber,
                codes,
                types,
                paidDateFrom,
                paidDateTo,
                direction,
                groupNumber,
                groupName,
                groupLocations
              }
            }))
          }}
          pagination={{
            current: filter.page + 1,
            pageSize: filter.perPage,
            total: query.data?.count ?? 0,
            pageSizeOptions: [10, 25, 50, 100],
            itemsLabel: 'transactions',
            onChange: (page: number, pageSize: number) => {
              setFilter(f => ({
                ...f,
                page: page - 1,
                perPage: pageSize
              }))
            }
          }}
        />
      </PageContainer>
    </HelpDrawerLayout>
  )
}
